import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ExternalLink, Maximize2, Minus, Plus, RotateCcw, RotateCw, X } from 'lucide-react';
import { cn } from '@/lib/cn';

/*
  ═════════════════════════════════════════════════════════════════════════════
  LOOKING PROPERLY AT A PHOTO

  Why a gate needs this
  ─────────────────────
  Every photo in this product is rendered at between 36 and 80 pixels, because
  it sits beside a name in a list. That is fine for recognising somebody you
  already know and useless for the one moment it actually matters: an officer
  holding the screen next to a child's face, deciding whether this is the same
  person. Thirty-six pixels of a collector's face is not evidence.

  So any photo can be opened, and once open it can be made big, turned the right
  way up, and moved around.

  Why rotate is not a nice-to-have
  ────────────────────────────────
  These photos are taken on phones, at a desk, in a hurry. A good proportion
  arrive sideways or upside down, because phones record orientation in EXIF and
  plenty of upload paths drop it. Without rotation the officer tilts their head,
  or the tablet, in front of a queue.

  Behaviour worth knowing
  ───────────────────────
  · Works with a mouse and with fingers — the same pointer handlers do drag and
    pinch, so a gate tablet needs no separate code path.
  · Escape closes. So does clicking the backdrop, but ONLY when the photo is at
    its normal size: once somebody has zoomed in, a click is how they drag, and
    closing on it loses their place.
  · Opens at "fit", not at whatever zoom the last photo was left at. A viewer
    that remembers state across photos shows the next officer a close-up of an
    ear.
  ═════════════════════════════════════════════════════════════════════════════
*/

const MIN_SCALE = 1;
const MAX_SCALE = 6;

const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n));

export function PhotoViewer({
  src,
  alt,
  caption,
  onClose,
}: {
  src: string;
  alt: string;
  /** A line under the controls — whose photo this is. */
  caption?: string;
  onClose: () => void;
}) {
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  const panelRef = useRef<HTMLDivElement>(null);
  /* Live pointers, so one finger pans and two pinch with no mode switch. */
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const pinchStart = useRef<{ distance: number; scale: number } | null>(null);
  const dragStart = useRef<{ x: number; y: number; offset: { x: number; y: number } } | null>(null);
  const moved = useRef(false);

  const reset = useCallback(() => {
    setScale(1);
    setRotation(0);
    setOffset({ x: 0, y: 0 });
  }, []);

  /* Zooming back out to fit re-centres: a photo left hanging half off the edge
     at 1× looks broken, and there is nowhere useful to pan to anyway. */
  const zoomTo = useCallback((next: number) => {
    const value = clamp(next, MIN_SCALE, MAX_SCALE);
    setScale(value);
    if (value === MIN_SCALE) setOffset({ x: 0, y: 0 });
  }, []);

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key === '+' || e.key === '=') zoomTo(scale + 0.5);
      if (e.key === '-' || e.key === '_') zoomTo(scale - 0.5);
      if (e.key === 'r') setRotation((r) => r + 90);
      if (e.key === '0') reset();
    };
    document.addEventListener('keydown', onKeyDown);

    /* The page behind must not scroll while this is open — on a tablet the
       drag would otherwise move the page instead of the photo. */
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus?.();
    };
  }, [onClose, reset, scale, zoomTo]);

  const distanceBetweenPointers = () => {
    const [a, b] = [...pointers.current.values()];
    if (!a || !b) return 0;
    return Math.hypot(a.x - b.x, a.y - b.y);
  };

  const onPointerDown = (e: React.PointerEvent) => {
    (e.target as Element).setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    moved.current = false;

    if (pointers.current.size === 2) {
      pinchStart.current = { distance: distanceBetweenPointers(), scale };
      dragStart.current = null;
    } else if (pointers.current.size === 1) {
      dragStart.current = { x: e.clientX, y: e.clientY, offset };
    }
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointers.current.size === 2 && pinchStart.current) {
      const distance = distanceBetweenPointers();
      if (pinchStart.current.distance > 0) {
        moved.current = true;
        zoomTo((distance / pinchStart.current.distance) * pinchStart.current.scale);
      }
      return;
    }

    /* One finger pans, but only when there is something to pan to. At fit the
       drag would just slide the photo off its own backdrop. */
    if (dragStart.current && scale > MIN_SCALE) {
      const dx = e.clientX - dragStart.current.x;
      const dy = e.clientY - dragStart.current.y;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) moved.current = true;
      setOffset({ x: dragStart.current.offset.x + dx, y: dragStart.current.offset.y + dy });
    }
  };

  const onPointerUp = (e: React.PointerEvent) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinchStart.current = null;
    if (pointers.current.size === 0) dragStart.current = null;
  };

  const onWheel = (e: React.WheelEvent) => {
    zoomTo(scale + (e.deltaY < 0 ? 0.3 : -0.3));
  };

  const quarterTurned = ((rotation / 90) % 2 + 2) % 2 === 1;

  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-modal="true"
      aria-label={alt}
      tabIndex={-1}
      className="fixed inset-0 z-[100] flex flex-col bg-black/92 outline-none animate-fade-in"
      onClick={(e) => {
        /* Backdrop only, and only when nothing is zoomed — see the note above. */
        if (e.target === e.currentTarget && scale === MIN_SCALE && !moved.current) onClose();
      }}
    >
      {/* ── Controls ──────────────────────────────────────────────────────── */}
      <div className="flex flex-shrink-0 items-center gap-1 px-3 py-2.5">
        <div className="mr-auto min-w-0">
          <p className="truncate text-[13px] font-bold text-white">{alt}</p>
          {caption && <p className="truncate text-[11px] text-white/60">{caption}</p>}
        </div>

        <ViewerButton label="Zoom out" onClick={() => zoomTo(scale - 0.5)} disabled={scale <= MIN_SCALE}>
          <Minus size={16} />
        </ViewerButton>
        <span className="w-[42px] text-center text-[11px] font-bold tabular-nums text-white/70">
          {Math.round(scale * 100)}%
        </span>
        <ViewerButton label="Zoom in" onClick={() => zoomTo(scale + 0.5)} disabled={scale >= MAX_SCALE}>
          <Plus size={16} />
        </ViewerButton>

        <span className="mx-1 h-5 w-px bg-white/20" />

        <ViewerButton label="Rotate left" onClick={() => setRotation((r) => r - 90)}>
          <RotateCcw size={16} />
        </ViewerButton>
        <ViewerButton label="Rotate right" onClick={() => setRotation((r) => r + 90)}>
          <RotateCw size={16} />
        </ViewerButton>

        <span className="mx-1 h-5 w-px bg-white/20" />

        <ViewerButton label="Reset" onClick={reset} disabled={scale === 1 && rotation === 0}>
          <Maximize2 size={16} />
        </ViewerButton>
        <ViewerButton label="Open the original in a new tab" href={src}>
          <ExternalLink size={16} />
        </ViewerButton>
        <ViewerButton label="Close" onClick={onClose}>
          <X size={18} />
        </ViewerButton>
      </div>

      {/* ── The photo ─────────────────────────────────────────────────────── */}
      <div
        className="flex flex-1 touch-none select-none items-center justify-center overflow-hidden p-3"
        /*
          The dark area AROUND the photo is the backdrop people actually click —
          the outer element is only the thin strips above and below, so putting
          the close there alone made clicking outside the photo do nothing.
        */
        onClick={(e) => {
          if (e.target === e.currentTarget && scale === MIN_SCALE && !moved.current) onClose();
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onWheel={onWheel}
        onDoubleClick={() => (scale > MIN_SCALE ? reset() : zoomTo(2.5))}
      >
        <img
          src={src}
          alt={alt}
          draggable={false}
          style={{
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale}) rotate(${rotation}deg)`,
            /* No animation while a finger is down, or the photo lags the hand. */
            transition: dragStart.current || pinchStart.current ? 'none' : 'transform 120ms ease-out',
          }}
          className={cn(
            'max-h-full max-w-full object-contain',
            /* A quarter turn swaps which axis is the long one, so the bounds
               have to swap with it or a portrait photo turned sideways is
               cropped by its own container. */
            quarterTurned && 'max-h-[88vw] max-w-[78vh]',
            scale > MIN_SCALE ? 'cursor-grab active:cursor-grabbing' : 'cursor-zoom-in',
          )}
        />
      </div>

      <p className="flex-shrink-0 px-3 pb-2.5 text-center text-[10.5px] text-white/45">
        Drag to move · scroll or pinch to zoom · double-click to fit · Esc to close
      </p>
    </div>,
    document.body,
  );
}

function ViewerButton({
  label,
  onClick,
  href,
  disabled,
  children,
}: {
  label: string;
  onClick?: () => void;
  href?: string;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  const className =
    'flex h-8 w-8 items-center justify-center rounded-md text-white/75 transition-colors hover:bg-white/15 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 disabled:pointer-events-none disabled:opacity-35';

  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={label}
        title={label}
        className={className}
      >
        {children}
      </a>
    );
  }
  return (
    <button type="button" aria-label={label} title={label} onClick={onClick} disabled={disabled} className={className}>
      {children}
    </button>
  );
}
